import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Small "Cere către FDP" button rendered on an empty catalog category card.
 * Clicking opens a dialog with an optional message; submitting persists a
 * wanted-profile request that FDP staff sees in the admin backlog tab.
 */
export default function WantedProfileButton({ simType, categorySlug, categoryName }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post("/wanted-profiles", {
        sim_type: simType,
        category_slug: categorySlug,
        message: message.trim().slice(0, 1000),
      });
      toast.success(t("wanted.toast_thanks", { category: categoryName }));
      setSent(true);
      setTimeout(() => {
        setOpen(false);
      }, 700);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("wanted.toast_error"),
      );
    } finally {
      setBusy(false);
    }
  };

  const stop = (e) => {
    e.stopPropagation();
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
          setSent(false);
        }}
        className="absolute bottom-2 left-2 right-2 px-2 py-1.5 rounded bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/30 text-orange-300 text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5"
        data-testid={`wanted-btn-${categorySlug}`}
      >
        <Send size={11} /> {t("wanted.btn")}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-zinc-950 border-orange-500/30 max-w-md"
          onClick={stop}
          data-testid="wanted-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white font-outfit">
              {t("wanted.dialog_title", { category: categoryName })}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              {t("wanted.dialog_desc", {
                type: simType === "INTERIOR" ? t("simulate.type_interior") : t("simulate.type_exterior"),
              })}
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div className="py-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
                <Send className="text-emerald-400" size={20} />
              </div>
              <p className="text-emerald-300 font-medium">{t("wanted.sent_title")}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {t("wanted.sent_body")}
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide block mb-2">
                  {t("wanted.msg_label")}
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("wanted.msg_placeholder")}
                  rows={4}
                  maxLength={1000}
                  className="bg-zinc-900 border-white/10 text-white text-sm resize-none focus-visible:ring-orange-500/40"
                  data-testid="wanted-message"
                />
                <div className="text-[10px] text-zinc-600 mt-1 text-right">
                  {message.length}/1000
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="text-zinc-400 hover:text-white"
                  data-testid="wanted-cancel"
                  disabled={busy}
                >
                  {t("wanted.cancel")}
                </Button>
                <Button
                  onClick={send}
                  disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="wanted-submit"
                >
                  {busy ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      {t("wanted.submit_loading")}
                    </>
                  ) : (
                    <>
                      <Send size={14} className="mr-2" />
                      {t("wanted.submit_btn")}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
